import { useEffect, useState } from "react";
import { Loader2, TrendingUp, FileDown, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const rate = (r) => (r === null || r === undefined ? "—" : `${r}%`);

export function GovReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/gov-opps/analytics").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (data === null) return <div className="mt-5 text-slate-400 text-center py-8" data-testid="gov-report-loading"><Loader2 className="animate-spin inline" size={20} /></div>;
  const s = data.summary || {};

  const downloadPdf = async () => {
    try {
      const res = await api.get("/admin/gov-opps/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "gov-bids-report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const cards = [
    { label: "Total Bids", value: s.total ?? 0, testid: "report-total" },
    { label: "Win Rate", value: rate(s.win_rate), sub: s.decided ? `${s.won} won / ${s.lost} lost` : "No decided bids yet", testid: "report-win-rate" },
    { label: "Won Value", value: money(s.won_value), testid: "report-won-value" },
    { label: "Active Pipeline", value: money(s.pipeline_value), sub: `${s.pipeline_count ?? 0} open bid${s.pipeline_count === 1 ? "" : "s"}`, testid: "report-pipeline" },
  ];

  return (
    <div className="mt-5" data-testid="gov-report-panel">
      <div className="flex justify-end">
        <button data-testid="report-download-pdf" onClick={downloadPdf}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]">
          <FileDown size={14} /> Download PDF
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold">{c.label}</div>
            <div className="mt-1.5 font-[Poppins] font-extrabold text-[26px] text-[var(--pg-navy)] leading-none">{c.value}</div>
            {c.sub && <div className="mt-1.5 text-[12px] text-slate-400">{c.sub}</div>}
          </div>
        ))}
      </div>

      {!s.decided && (
        <p data-testid="report-empty-note" className="mt-4 text-[13px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          Win rates appear once bids are marked <strong>Won</strong> or <strong>Lost</strong> on the board.
        </p>
      )}

      <div className="mt-5 grid lg:grid-cols-2 gap-5 items-start">
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="report-by-agency">
          <div className="px-5 pt-4 pb-1 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><TrendingUp size={15} /> By Agency</div>
          <table className="w-full text-left text-[13px] min-w-[460px]">
            <thead>
              <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
                <th className="px-5 py-2.5">Agency</th><th className="px-3 py-2.5 text-right">Bids</th><th className="px-3 py-2.5 text-right">W / L</th>
                <th className="px-3 py-2.5 text-right">Win Rate</th><th className="px-5 py-2.5 text-right">Est. Value</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_agency || []).length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">No bids tracked yet.</td></tr>}
              {(data.by_agency || []).map((a) => (
                <tr key={a.agency} className="border-b border-slate-100" data-testid={`report-agency-row-${a.agency}`}>
                  <td className="px-5 py-2.5 font-medium text-[var(--pg-navy)]">{a.agency}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{a.total}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{a.won} / {a.lost}</td>
                  <td className="px-3 py-2.5 text-right font-[Poppins] font-semibold">{rate(a.win_rate)}</td>
                  <td className="px-5 py-2.5 text-right text-slate-500">{money(a.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="report-by-value">
          <div className="px-5 pt-4 pb-1 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><TrendingUp size={15} /> By Bid Value</div>
          <table className="w-full text-left text-[13px] min-w-[420px]">
            <thead>
              <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
                <th className="px-5 py-2.5">Value Band</th><th className="px-3 py-2.5 text-right">Bids</th><th className="px-3 py-2.5 text-right">W / L</th>
                <th className="px-5 py-2.5 text-right">Win Rate</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_value || []).map((b) => (
                <tr key={b.band} className={`border-b border-slate-100 ${b.total === 0 ? "opacity-50" : ""}`} data-testid={`report-band-row-${b.band}`}>
                  <td className="px-5 py-2.5 font-medium text-[var(--pg-navy)]">{b.band}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{b.total}</td>
                  <td className="px-3 py-2.5 text-right text-slate-500">{b.won} / {b.lost}</td>
                  <td className="px-5 py-2.5 text-right font-[Poppins] font-semibold">{rate(b.win_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MonthlyTrend months={data.by_month || []} />
    </div>
  );
}

function MonthlyTrend({ months }) {
  const max = Math.max(1, ...months.map((m) => Math.max(m.added, m.won)));
  const hasData = months.some((m) => m.added || m.won || m.lost);
  const label = (k) => new Date(k + "-15").toLocaleDateString("en-US", { month: "short" });
  return (
    <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="report-monthly-trend">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><CalendarDays size={15} /> Monthly Trend</div>
        <div className="flex items-center gap-4 text-[11.5px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300"></span> Bids added</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500"></span> Won</span>
        </div>
      </div>
      {!hasData && <p className="mt-4 text-[13px] text-slate-400" data-testid="trend-empty-note">Activity will chart here as bids are added and decided.</p>}
      <div className="mt-4 flex items-end gap-1.5 h-28">
        {months.map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`trend-month-${m.month}`}>
            <div className="w-full flex items-end justify-center gap-0.5 h-20">
              <div className="w-2/5 bg-slate-300 rounded-t" title={`${m.added} added`} style={{ height: `${(m.added / max) * 100}%`, minHeight: m.added ? 4 : 0 }} />
              <div className="w-2/5 bg-green-500 rounded-t" title={`${m.won} won`} style={{ height: `${(m.won / max) * 100}%`, minHeight: m.won ? 4 : 0 }} />
            </div>
            <span className="text-[9.5px] text-slate-400 font-[Poppins]">{label(m.month)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
